import { config } from './config';
import {
  ContactUsResponse,
  Country,
  County,
  ImeiValidateResponse,
  PhoneData,
  PlaceOrderResponse,
  PromoDeviceProps,
  PromoEvents,
} from './types';
import { getPurchasedDate } from '../utils/getPurchasedDate';

function getHeaderData() {
  return {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  };
}

export function getRecaptchaKey(): Promise<string> {
  return fetch(`${config.apiUrl}/get-recaptcha-key`)
    .then((response) => response.json())
    .then((data) => data.key);
}

export function getCountries(): Promise<ReadonlyArray<Country>> {
  return fetch(`${config.publicUrl}/data/countries.json`).then((response) => response.json());
}

export function getCounties(): Promise<ReadonlyArray<County>> {
  return fetch(`${config.publicUrl}/data/counties.json`).then((response) => response.json());
}

export function getRetailers(): Promise<ReadonlyArray<string>> {
  return fetch(`${config.apiUrl}/promotion/retailers`, getHeaderData())
    .then((response) => response.json())
    .then((data) => data.data);
}

export function getPromoDevices(): Promise<PromoDeviceProps[]> {
  return fetch(`${config.apiUrl}/promotion/promo-devices`, getHeaderData())
    .then((response) => response.json())
    .then((data) => data.data);
}

export function getEligibleDevices(): Promise<PhoneData[]> {
  return fetch(`${config.apiUrl}/promotion/eligible-tradein-devices-for-enhancements`, getHeaderData())
    .then((response) => response.json())
    .then((data) => data.data);
}

export function getEvents(): Promise<PromoEvents> {
  return fetch(`${config.apiUrl}/promotion/status`)
    .then((response) => response.json())
    .then((data) => data.data);
}

export function getVendors(): Promise<ReadonlyArray<string>> {
  return fetch(`${config.apiUrl}/vendors`, getHeaderData())
    .then((response) => response.json())
    .then((data) => data.data);
}

export function getPhoneModels(
  vendorName: string,
  imei: string,
  purchasedDateStamp: number | null
): Promise<ReadonlyArray<PhoneData>> {
  const params = new URLSearchParams();
  if (imei) {
    params.set('imei', imei);
  }
  if (purchasedDateStamp) {
    params.set('purchased_date', getPurchasedDate(purchasedDateStamp));
  }
  const queryStr = imei || purchasedDateStamp ? `?${params.toString()}` : '';

  return fetch(`${config.apiUrl}/devices/${vendorName}${queryStr}`, getHeaderData())
    .then((response) => response.json())
    .then((data) => data.data);
}

export function getEligiblePhoneModels(vendorName: string): Promise<ReadonlyArray<PhoneData>> {
  return fetch(`${config.apiUrl}/eligible-devices/${vendorName}`, getHeaderData())
    .then((response) => response.json())
    .then((data) => data.data);
}

export function placeOrder<T extends object = object>(formData: T): Promise<PlaceOrderResponse> {
  return fetch(`${config.apiUrl}/order`, {
    ...getHeaderData(),
    method: 'POST',
    body: JSON.stringify(formData),
  }).then((response) => {
    const isResponseValid = response.status === 200 || response.status === 201;
    if (response.status === 422 || response.status === 429) {
      return response.json().then((data) => ({ ...data, responseStatus: response.status }));
    } else if (!isResponseValid) {
      throw new Error('Your details validation failed');
    }
    return response.json().then((data) => ({ ...data, responseStatus: response.status }));
  });
}

export function validateImei<T extends object = object>(formData: T): Promise<ImeiValidateResponse> {
  return fetch(`${config.apiUrl}/imei-check`, {
    ...getHeaderData(),
    method: 'POST',
    body: JSON.stringify(formData),
  }).then((response) => {
    const isResponseValid = response.status === 200 || response.status === 201;
    if (response.status === 422 || response.status === 429) {
      return response.json().then((data) => ({ ...data, responseStatus: response.status }));
    } else if (!isResponseValid) {
      throw new Error('Imei validation failed');
    }
    return response.json().then((data) => ({ ...data, responseStatus: response.status }));
  });
}

export function sendContact<T extends object = object>(formData: T): Promise<ContactUsResponse> {
  return fetch(`${config.apiUrl}/contact`, {
    ...getHeaderData(),
    method: 'POST',
    body: JSON.stringify(formData),
  }).then((response) => {
    const isResponseValid = response.status === 200 || response.status === 201;
    if (response.status === 422 || response.status === 429) {
      return response.json().then((data) => ({ ...data, responseStatus: response.status }));
    } else if (!isResponseValid) {
      throw new Error('Request is failed');
    }
    return response.json().then((data) => ({ ...data, responseStatus: response.status }));
  });
}
